import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import avatar_default from 'assets/img/default-avatar.png';
import { Avatar, Box, ClickAwayListener, Grid, Tooltip } from '@mui/material';
import MailOutline from '@mui/icons-material/MailOutline';
import ParentCommentComponent from './ParentCommentComponent';
import DocumentCommentActionsComponent from './DocumentCommentActionsComponent';
import i18n from 'i18next';
import { Card, Typography } from '@totalsoft/rocket-ui';
import { ThemedTypography } from 'components/common/CommonStyles';
import {
  CommentCardWrapper,
  CommentCurrentUserChip,
  CommentRecipientsAnchor,
  CommentRecipientsTooltip,
  CommentTypeChip
} from './CommentsStyles';

function DocumentCommentComponent({
  comment,
  parentComment,
  onEditComment,
  onReplyComment,
  onDeleteComment,
  addToRefs,
  handleClickToScroll
}) {
  const { t } = useTranslation();
  const [openMailedPeople, setOpenMailedPeople] = useState(false);

  const handleTooltipClose = () => {
    setOpenMailedPeople(false);
  };

  const handleTooltipOpen = () => {
    setOpenMailedPeople(true);
  };

  return (
    <CommentCardWrapper id={`comment_${comment.commentId}`} ref={ref => addToRefs(ref, comment.commentId)}>
      <Card disablePadding>
        <Grid container gap={1} p={2}>
          <Grid item container gap={1} alignItems='center' justifyContent='space-between' flexWrap='nowrap'>
            <Grid item display='flex' alignItems='center' gap={0.75}>
              <Avatar src={comment.avatar ? comment.avatar : avatar_default} alt='name' sx={{ width: 24, height: 24 }} />
              <Tooltip title={comment.userName}>
                <div>
                  <Typography maxWidth='100px' fontWeight='bold' variant='subtitle2' noWrap>
                    {comment.userName}
                  </Typography>
                </div>
              </Tooltip>
              {comment.isCurrentUser && <CommentCurrentUserChip color='primary' size='small' label={t('Offer.Comments.CurrentUserTag')} />}
              <ThemedTypography color='grey'>
                {new Date(comment.date).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
              </ThemedTypography>
              {comment.edited && (
                <ThemedTypography color='grey' emphasis='bold'>
                  {t('Offer.Comments.Edited')}
                </ThemedTypography>
              )}
            </Grid>
            <Grid item display='flex' alignItems='center'>
              <CommentTypeChip
                color={comment.internal ? 'primary' : 'warning'}
                internal={comment.internal}
                variant='outlined'
                size='small'
                label={comment.internal ? t('Offer.Comments.Internal') : t('Offer.Comments.Everyone')}
              />
              <DocumentCommentActionsComponent
                comment={comment}
                onReplyComment={onReplyComment}
                onEditComment={onEditComment}
                onDeleteComment={onDeleteComment}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {parentComment && (
              <Box mb={1} sx={{ cursor: 'pointer' }} onClick={() => handleClickToScroll(parentComment.commentId)}>
                <ParentCommentComponent comment={{ ...parentComment, commentText: comment.parentCommentText }} />
              </Box>
            )}
            <ThemedTypography color='grey' whiteSpace='break-spaces'>
              {comment.deleted ? t('Offer.Comments.MessageDeleted') : comment.commentText}
            </ThemedTypography>
          </Grid>
          {comment.recipients.length > 0 && (
            <Grid item xs={12}>
              <Grid container justifyContent='flex-end' alignItems='center' gap={1}>
                <ThemedTypography color='grey'>{t('Offer.Comments.MailSentTo')}</ThemedTypography>
                <ClickAwayListener onClickAway={handleTooltipClose}>
                  <div>
                    <CommentRecipientsTooltip
                      onClose={handleTooltipClose}
                      open={openMailedPeople}
                      disableFocusListener
                      disableHoverListener
                      title={comment.recipients.map((receipt, index) => (
                        <Grid key={index} container alignItems='center' px={1} py={0.5} gap={1}>
                          <MailOutline sx={{ width: 24, height: 24 }} />
                          <Typography>{receipt}</Typography>
                        </Grid>
                      ))}
                      placement='bottom-end'
                      interactive
                    >
                      <CommentRecipientsAnchor onClick={handleTooltipOpen}>
                        {t(comment.recipients.length > 1 ? 'Offer.Comments.MailedRecipientsTag' : 'Offer.Comments.MailedRecipientTag', {
                          numberOfPeople: comment.recipients.length
                        })}
                      </CommentRecipientsAnchor>
                    </CommentRecipientsTooltip>
                  </div>
                </ClickAwayListener>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Card>
    </CommentCardWrapper>
  );
}

DocumentCommentComponent.propTypes = {
  comment: PropTypes.object.isRequired,
  parentComment: PropTypes.object,
  onReplyComment: PropTypes.func.isRequired,
  onEditComment: PropTypes.func.isRequired,
  onDeleteComment: PropTypes.func.isRequired,
  addToRefs: PropTypes.func.isRequired,
  handleClickToScroll: PropTypes.func.isRequired
};

export default DocumentCommentComponent;
